import { memo } from 'react';
import { Switch } from '@fluentui/react-components';
import { useAppStore } from '@/stores/appStore';
import { useSettingCardStyles } from './sectionStyles';

/**
 * Privacy controls: telemetry opt-out and crash reporting opt-out.
 *
 * `telemetryService` reads `telemetryEnabled` and `crashReportsEnabled`
 * from the app store on every call, so toggling these switches takes
 * effect immediately for the rest of the session — no reload needed.
 * The values persist across reloads via zustand `persist`.
 */
export const PrivacySection = memo(() => {
  const styles = useSettingCardStyles();
  // Subscribe per-field — see AppearanceSection for rationale.
  const telemetryEnabled = useAppStore((s) => s.telemetryEnabled);
  const setTelemetryEnabled = useAppStore((s) => s.setTelemetryEnabled);
  const crashReportsEnabled = useAppStore((s) => s.crashReportsEnabled);
  const setCrashReportsEnabled = useAppStore((s) => s.setCrashReportsEnabled);

  return (
    <div className={styles.list}>
      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Send anonymous usage telemetry</p>
          <p className={styles.cardHelp}>
            Helps us understand which features are used. Never includes workflow content,
            connection secrets, or personally identifiable information.
          </p>
        </div>
        <div className={styles.cardControl}>
          <Switch
            checked={telemetryEnabled}
            onChange={(_, data) => setTelemetryEnabled(data.checked)}
            aria-label="Send anonymous usage telemetry"
          />
        </div>
      </div>

      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Crash reports</p>
          <p className={styles.cardHelp}>
            Send stack traces and recent breadcrumbs when the portal crashes so we can diagnose the issue.
          </p>
        </div>
        <div className={styles.cardControl}>
          <Switch
            checked={crashReportsEnabled}
            onChange={(_, data) => setCrashReportsEnabled(data.checked)}
            aria-label="Send crash reports"
          />
        </div>
      </div>
    </div>
  );
});
PrivacySection.displayName = 'PrivacySection';
